import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, Rocket, CheckCircle2, Mail, Phone, Building2, Globe, Zap, Trophy, Send } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const PRIMARY = "#6B2D5C";
const SECONDARY = "#EC1F7C";
const BG_DEEP = "#160732";

const PERKS = [
  { icon: Rocket, title: "Lancement express", text: "Votre site en ligne en quelques semaines, conçu sur mesure pour votre activité." },
  { icon: Zap, title: "Outils Cockpit inclus", text: "Réservations, devis, paiement, click & collect, CRM partenaires, statistiques temps réel." },
  { icon: Trophy, title: "Design qui claque", text: "Identité graphique forte, animations cinéma, mobile-first." },
  { icon: Sparkles, title: "Suivi & accompagnement", text: "Une équipe dédiée pour faire grandir votre projet, comme pour le club d'Istres." },
];

export default function IphOnboardingPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    website: "",
    project: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Merci de renseigner votre nom, email et téléphone.");
      return;
    }
    setSending(true);
    try {
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        type: "contact",
        source: "istres-handball-onboarding",
        page: "/lancez-votre-site",
        message:
          `[Lead provenant du site ISTRES PROVENCE HANDBALL]\n\n` +
          `Demande : Création de site avec WeCockpit\n` +
          `Société : ${form.company || "—"}\n` +
          `Site existant : ${form.website || "—"}\n\n` +
          `Projet :\n${form.project || "(non renseigné)"}\n`,
        meta: {
          origin: "ISTRES HANDBALL",
          agency_hub_target: true,
          channel: "iph_footer_credit",
          company: form.company,
          website: form.website,
        },
      });
      setDone(true);
      toast.success("Message envoyé ! Notre équipe vous recontacte sous 48h.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-20" style={{ background: `linear-gradient(135deg, ${BG_DEEP}, ${PRIMARY})` }} data-testid="iph-onboarding-success">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="max-w-xl w-full bg-white rounded-3xl shadow-2xl p-10 text-center">
          <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center text-white shadow-xl" style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff6ba4)` }}>
            <CheckCircle2 size={42}/>
          </div>
          <h1 className="font-blackhawk text-4xl uppercase italic mt-6" style={{ color: PRIMARY }}>Message envoyé !</h1>
          <p className="text-slate-600 mt-3 leading-relaxed">
            Merci <span className="font-bold">{form.name}</span>. Nous avons bien reçu votre demande et nous vous recontactons dans les <strong>48h ouvrées</strong> pour échanger sur votre projet.
          </p>
          <Link to="/sport" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-105 transition" style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }} data-testid="iph-onboarding-back-home">
            <ArrowLeft size={14}/> Retour au site IPH
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: BG_DEEP }} data-testid="iph-onboarding-page">
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none opacity-60" style={{
        background: `radial-gradient(ellipse at 20% 0%, rgba(236,31,124,0.25), transparent 50%), radial-gradient(ellipse at 80% 100%, rgba(107,45,92,0.4), transparent 60%)`,
      }}/>
      {/* Grain */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22 viewBox=%220 0 120 120%22><circle cx=%221%22 cy=%221%22 r=%221%22 fill=%22%23ffffff%22/></svg>')]"/>

      {/* Header */}
      <header className="relative z-10 px-4 sm:px-8 pt-6">
        <Link to="/sport" className="inline-flex items-center gap-2 text-white/70 hover:text-pink-400 transition text-xs font-black tracking-widest uppercase" data-testid="iph-onboarding-back">
          <ArrowLeft size={14}/> Retour Istres Handball
        </Link>
      </header>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-14 grid lg:grid-cols-2 gap-12 items-start">
        {/* LEFT — pitch */}
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-500/15 border border-pink-400/30 text-pink-300 font-black uppercase text-[10px] tracking-[0.3em]">
            <Sparkles size={11}/> Une création WeCockpit × Agence MSC
          </div>
          <h1 className="font-blackhawk text-5xl sm:text-6xl lg:text-7xl uppercase italic text-white leading-[0.95] mt-5">
            Ce site vous<br/>
            a <span style={{ color: SECONDARY }}>plu ?</span>
          </h1>
          <p className="text-2xl sm:text-3xl font-bebas text-pink-300 tracking-widest mt-3">
            Lancez le vôtre.
          </p>
          <p className="text-white/70 text-base sm:text-lg mt-6 max-w-lg leading-relaxed">
            Le site officiel de l'<strong className="text-white">Istres Provence Handball</strong> a été conçu sur la plateforme <strong className="text-white">WeCockpit</strong> par l'<strong className="text-white">Agence MSC</strong>. Le même studio peut créer le site qui propulsera <em>votre</em> activité.
          </p>

          {/* Perks */}
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            {PERKS.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.08 }}
                className="rounded-2xl bg-white/5 border border-white/10 p-4 backdrop-blur"
              >
                <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white shadow-md" style={{ background: `linear-gradient(135deg, ${SECONDARY}, ${PRIMARY})` }}>
                  <p.icon size={16}/>
                </div>
                <div className="font-black text-white text-sm uppercase tracking-tight mt-3">{p.title}</div>
                <div className="text-white/60 text-xs mt-1.5 leading-relaxed">{p.text}</div>
              </motion.div>
            ))}
          </div>

          {/* Trust line */}
          <div className="mt-10 flex flex-wrap items-center gap-3 text-[11px] text-white/50 font-black tracking-widest uppercase">
            <span className="px-3 py-1.5 rounded-md bg-white/5 border border-white/10">Site IPH 2025-2026</span>
            <span>·</span>
            <span className="px-3 py-1.5 rounded-md bg-white/5 border border-white/10">+50 sites sportifs</span>
            <span>·</span>
            <span className="px-3 py-1.5 rounded-md bg-white/5 border border-white/10">Made in France</span>
          </div>
        </motion.div>

        {/* RIGHT — form card */}
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="bg-white rounded-3xl shadow-[0_50px_100px_rgba(236,31,124,0.18)] border border-white/20 p-7 sm:p-9 lg:sticky lg:top-8"
          data-testid="iph-onboarding-form"
        >
          <div className="text-pink-600 font-black tracking-[0.3em] text-[10px] uppercase">Contactez l'équipe</div>
          <h2 className="font-blackhawk text-3xl uppercase italic mt-1" style={{ color: PRIMARY }}>
            Parlez-nous de votre projet
          </h2>
          <p className="text-slate-500 text-sm mt-2">Réponse personnalisée sous <strong>48h ouvrées</strong>. Aucun engagement.</p>

          <div className="mt-6 space-y-4">
            <Field icon={Building2} label="Nom complet *" value={form.name} onChange={(v) => set("name", v)} placeholder="Prénom Nom" testId="iph-form-name"/>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field icon={Mail} type="email" label="Email *" value={form.email} onChange={(v) => set("email", v)} placeholder="vous@exemple.fr" testId="iph-form-email"/>
              <Field icon={Phone} type="tel" label="Téléphone *" value={form.phone} onChange={(v) => set("phone", v)} placeholder="06 12 34 56 78" testId="iph-form-phone"/>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field icon={Building2} label="Société / Club" value={form.company} onChange={(v) => set("company", v)} placeholder="Nom de votre structure" testId="iph-form-company"/>
              <Field icon={Globe} label="Site actuel" value={form.website} onChange={(v) => set("website", v)} placeholder="https://…" testId="iph-form-website"/>
            </div>
            <div>
              <label className="block text-[11px] font-black tracking-widest uppercase text-slate-500 mb-1.5">Votre projet</label>
              <textarea
                rows={4}
                value={form.project}
                onChange={(e) => set("project", e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-pink-400 focus:ring-2 focus:ring-pink-200 px-4 py-3 text-sm text-slate-700 transition outline-none resize-none"
                placeholder="Décrivez en quelques lignes votre activité, vos besoins, vos inspirations…"
                data-testid="iph-form-project"
              />
            </div>
          </div>

          <button type="submit" disabled={sending}
            className="mt-7 w-full inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full font-black uppercase text-sm tracking-widest text-white shadow-2xl hover:scale-[1.02] transition disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}
            data-testid="iph-form-submit">
            {sending ? "Envoi en cours…" : (<>Lancer mon projet <Send size={15}/></>)}
          </button>

          <p className="text-[10px] text-slate-400 mt-4 text-center leading-relaxed">
            En soumettant ce formulaire, vous acceptez d'être recontacté(e) par l'Agence MSC / WeCockpit. <br/>Vos données ne sont jamais partagées avec un tiers.
          </p>
        </motion.form>
      </div>

      {/* Bottom signature */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 pb-8 mt-4">
        <div className="flex flex-wrap items-center justify-center gap-4 text-white/40 text-[11px] font-black tracking-[0.3em] uppercase">
          <span>WeCockpit</span>
          <span className="text-white/20">×</span>
          <span>Agence MSC</span>
          <span className="text-white/20">·</span>
          <span>Lead origine : ISTRES HANDBALL</span>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="block text-[11px] font-black tracking-widest uppercase text-slate-500 mb-1.5">{label}</label>
      <div className="relative">
        <Icon size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-pink-400 focus:ring-2 focus:ring-pink-200 pl-10 pr-4 py-3 text-sm text-slate-700 transition outline-none"
          data-testid={testId}
        />
      </div>
    </div>
  );
}
